import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GitCommitVertical, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import RelatedLinks from "@/components/common/RelatedLinks";

/**
 * Public product updates - a recency/trust signal that is only worth anything
 * if it is true.
 *
 * SOURCING RULE FOR THIS FILE
 * Every entry below corresponds to work that is actually in this repository,
 * and every date is taken from the project's own commit history (earliest
 * commit 2026-08-31). Nothing is backdated, padded or invented. When you ship
 * something new, add a dated entry at the top - do not write an entry before
 * the change exists, and do not claim outcomes or numbers here.
 */
const UPDATES = [
  {
    date: "2026-09-21",
    title: "Clearer homepage, and honest trust signals",
    items: [
      "Reworked the homepage hero so the first line says what Sponsor Me actually does, with Browse Sponsors and Become a Sponsor side by side.",
      "Added a 'See How Sponsor Me Works' walkthrough built from the real interface, using clearly-labelled example data only.",
      "Added a founder note, a Trust & Safety summary and this Updates page.",
      "Built the plumbing to collect real member quotes later. Publication consent is required, you choose first name, initials or anonymous, and an admin reviews it first. No testimonials are published yet.",
    ],
  },
  {
    date: "2026-09-20",
    title: "Human sponsor vs AI comparison page, and better analytics",
    items: [
      "Published /vs/ai-sponsor, a factual comparison of human peer sponsorship and AI recovery companions.",
      "Expanded privacy-safe analytics to cover navigation between pages, so we can see which pages help people and which do not.",
      "Ran a full regression sweep across the API and every page.",
    ],
  },
  {
    date: "2026-09-19",
    title: "Safety FAQ, live sponsor counts, About page and SEO",
    items: [
      "Added the sponsor safety and FAQ section to the homepage, including the honest answer that sponsors are not background checked or certified.",
      "Sponsor counts on the homepage now come live from the database, with proper loading, empty and error states.",
      "Published the About page with the real founder story.",
      "Reworked the pricing page and added social metadata and structured data for search engines.",
      "Installed privacy-safe product analytics.",
    ],
  },
  {
    date: "2026-09-13",
    title: "Admin insight tools",
    items: [
      "Added a signup funnel view and site traffic panel to the admin area, so drop-off can be found and fixed.",
    ],
  },
  {
    date: "2026-09-02",
    title: "Memberships and first public deployment",
    items: [
      "Wired up Stripe webhooks so memberships stay in sync automatically.",
      "Deployed Sponsor Me publicly for the first time.",
    ],
  },
  {
    date: "2026-08-31",
    title: "Sponsor Me begins",
    items: [
      "First working version: sponsor profiles, browsing, sponsorship requests, private messaging, block and report tools, and privacy-first accounts with no legal name required.",
    ],
  },
];

function formatDate(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export default function Updates() {
  return (
    <div className="bg-[#FBF9F5]" data-testid="updates-page">
      <section className="max-w-4xl mx-auto px-4 pt-8 pb-8">
        <Breadcrumbs items={[{ label: "Product Updates" }]} />
        <p className="mt-5 text-sm font-bold uppercase tracking-[0.14em] text-[#1F6F78]">
          Building Sponsor Me in public
        </p>
        <h1
          data-testid="updates-heading"
          className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#1F2A2E]"
        >
          Product Updates
        </h1>
        <p data-testid="updates-definition" className="mt-4 text-[#1F2A2E] leading-8 font-medium">
          Product updates are the dated record of changes that have actually shipped on Sponsor Me.
        </p>
        <p className="mt-3 text-[#4B5A60] leading-8">
          Sponsor Me is built and maintained by one person, in the open. Every entry below is a real
          change with a real date. There are no roadmap promises and no backdated history.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button asChild className="bg-[#1F6F78] hover:bg-[#185B62] text-white h-11 px-6">
            <Link to="/sponsors" data-testid="updates-browse-cta">
              Browse Sponsors <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="h-11 px-6 bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0]"
          >
            <Link to="/about" data-testid="updates-about-cta">
              Read the founder story
            </Link>
          </Button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 pb-16">
        <ol className="relative border-l border-[#E3DED4] ml-3" data-testid="updates-list">
          {UPDATES.map((u) => (
            <li key={u.date} data-testid={`update-entry-${u.date}`} className="ml-6 pb-8">
              <span className="absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full bg-[#D7EEF0] border border-[#B9DFE3]">
                <GitCommitVertical className="w-3.5 h-3.5 text-[#1F6F78]" aria-hidden="true" />
              </span>
              <time
                dateTime={u.date}
                className="text-xs font-bold uppercase tracking-wide text-[#7A868B]"
              >
                {formatDate(u.date)}
              </time>
              <h2 className="mt-1 font-display text-xl font-bold text-[#1F2A2E]">{u.title}</h2>
              <ul className="mt-3 space-y-2">
                {u.items.map((item) => (
                  <li
                    key={item}
                    className="flex gap-2.5 text-sm text-[#4B5A60] leading-6 rounded-xl bg-white border border-[#E3DED4] px-4 py-3"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>

        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="w-10 h-10 shrink-0 rounded-xl bg-[#D7EEF0] text-[#1F6F78] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" aria-hidden="true" />
          </span>
          <p className="text-sm text-[#4B5A60] leading-6">
            Spotted something broken, or want to suggest an improvement?{" "}
            <Link
              to="/contact"
              data-testid="updates-contact-link"
              className="font-semibold text-[#1F6F78] hover:underline"
            >
              Send a message
            </Link>{" "}
            - it reaches the person who builds this.
          </p>
        </div>
      </section>
      <RelatedLinks
        heading="Keep reading"
        links={[
          { to: "/about", label: "About Sponsor Me" },
          { to: "/sponsors", label: "Browse sponsors" },
          { to: "/contact", label: "Suggest an improvement" },
        ]}
      />
    </div>
  );
}
